/**
 * src/data/programs.js — chương trình, cuộc thi và gói hỗ trợ có hạn nộp.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * KHÁC GÌ VỚI `funds.js`
 *
 * Quỹ nhận hồ sơ quanh năm và khẩu vị của họ tương đối ổn định. Chương trình
 * thì mở rồi đóng theo đợt — và mỗi đợt là một sự kiện riêng với hạn nộp,
 * điều kiện và mức hỗ trợ riêng.
 *
 * Quy tắc phân loại: nhận hồ sơ liên tục thì vào `funds.js`; có đợt và hạn nộp
 * thì vào đây. Tín dụng đám mây nằm ở đây dù nhận liên tục, vì chúng không
 * phải nguồn vốn theo nghĩa thông thường và điều kiện gắn với chương trình.
 *
 * QUY TẮC DỮ LIỆU giữ nguyên như với quỹ: chỉ điền những gì nguồn chính thức
 * nói rõ, không suy đoán, mọi mục phải có nguồn và ngày kiểm chứng.
 *
 * TỪ 09/2026: chương trình được thêm và sửa ở trang quản trị /admin/chuong-trinh
 * (lưu trên Firestore). Danh sách dưới đây là dữ liệu gốc và dự phòng; bản trên
 * Firestore cùng programId sẽ thay thế hoặc ẩn bản trong code.
 *
 * `edition` là đợt gần nhất đã biết. Đợt đã đóng KHÔNG bị xoá — biết kỳ trước
 * mở khi nào giúp người dùng đoán được kỳ sau và chuẩn bị trước.
 * ─────────────────────────────────────────────────────────────────────────
 */

import REMOTE_PROGRAMS from './programsRemote.js';
import { gopChuongTrinh } from '../lib/programModel.js';

const usd = (n) => ({ amount: Math.round(n * 100), ccy: 'USD', fx: 26170, fxDate: '2026-09-09' });
const trieu = (n) => ({ amount: Math.round(n * 1e6), ccy: 'VND', fx: null, fxDate: null });
const eur = (n) => ({ amount: Math.round(n * 100), ccy: 'EUR', fx: 30500, fxDate: '2026-09-11' });

export const PROGRAMS_GOC = [
  /* ───────────── Tín dụng đám mây — nhận liên tục ───────────── */

  {
    programId: 'google-startups-cloud',
    name: 'Google for Startups Cloud Program',
    shortName: 'Google Cloud',
    slug: 'google-for-startups-cloud',
    organizer: 'Google Cloud',
    kind: 'CLOUD_CREDITS',
    website: 'https://cloud.google.com/startup',
    publish: { status: 'PUBLISHED' },

    edition: { label: 'Nhận liên tục', rolling: true },

    benefit: {
      capitalTypes: ['IN_KIND'],
      // Bậc Start tới 2 nghìn USD; bậc Scale tới 200 nghìn USD, riêng hướng AI
      // tới 350 nghìn USD trong hai năm.
      max: usd(350_000),
      note:
        'Tín dụng dùng cho dịch vụ Google Cloud, không quy đổi thành tiền mặt '
        + 'và không áp dụng cho mọi dịch vụ bên thứ ba.',
    },

    eligibility: {
      stages: ['PROTOTYPE', 'PRESEED', 'SEED', 'SERIES_A'],
      geos: ['GLOBAL'],
      industries: ['SAAS_B2B', 'AI', 'FINTECH', 'HEALTHTECH', 'WEB3'],
      hardRequirements: [
        'Doanh nghiệp công nghệ số, còn trong giới hạn tuổi công ty theo từng bậc',
        'Bậc Scale yêu cầu đã gọi được vốn từ quỹ đầu tư',
        'Chưa nhận quá ngưỡng tín dụng của chương trình trước đó',
      ],
    },

    process: { applyUrl: 'https://cloud.google.com/startup' },

    content: {
      summary:
        'Chương trình cấp tín dụng sử dụng dịch vụ Google Cloud cho startup, '
        + 'chia theo hai bậc tuỳ mức độ trưởng thành của doanh nghiệp.',
      tips: [
        'Tín dụng không phải tiền mặt. Nó giảm chi phí hạ tầng chứ không thay '
        + 'được vốn lưu động, nên đừng đưa vào kế hoạch dòng tiền như một khoản thu.',
      ],
    },

    provenance: {
      sources: [
        { url: 'https://cloud.google.com/startup/benefits', fetchedAt: '2026-09-11', note: 'Trang quyền lợi chương trình' },
        { url: 'https://cloud.google.com/terms/startup-program-tos', fetchedAt: '2026-09-11', note: 'Điều khoản chương trình' },
      ],
      lastVerifiedAt: '2026-09-11',
    },
  },

  {
    programId: 'aws-activate',
    name: 'AWS Activate',
    shortName: 'AWS Activate',
    slug: 'aws-activate',
    organizer: 'Amazon Web Services',
    kind: 'CLOUD_CREDITS',
    website: 'https://aws.amazon.com/startups/credits/',
    publish: { status: 'PUBLISHED' },

    edition: { label: 'Nhận liên tục', rolling: true },

    benefit: {
      capitalTypes: ['IN_KIND'],
      // Bậc Founders tới 5 nghìn USD; bậc Portfolio tới 200 nghìn USD.
      max: usd(200_000),
      note: 'Tín dụng dùng cho dịch vụ AWS, không quy đổi thành tiền mặt.',
    },

    eligibility: {
      stages: ['PRESEED', 'SEED', 'SERIES_A'],
      geos: ['GLOBAL'],
      industries: ['SAAS_B2B', 'AI', 'FINTECH', 'HEALTHTECH', 'GAMING'],
      hardRequirements: [
        'Doanh nghiệp thành lập trong vòng 10 năm và chưa qua vòng Series B',
        'Có tài khoản AWS hợp lệ ở gói trả phí',
        'Bậc Portfolio cần được một đơn vị đối tác của chương trình giới thiệu',
      ],
    },

    process: { applyUrl: 'https://aws.amazon.com/startups/credits/' },

    content: {
      summary:
        'Chương trình cấp tín dụng sử dụng dịch vụ AWS. Bậc Founders nộp trực '
        + 'tiếp; bậc Portfolio cần qua đơn vị đối tác giới thiệu.',
    },

    provenance: {
      sources: [
        { url: 'https://aws.amazon.com/startups/credits/', fetchedAt: '2026-09-11', note: 'Trang chương trình chính thức' },
      ],
      lastVerifiedAt: '2026-09-11',
    },
  },

  /* ───────────── Tài trợ và chương trình theo đợt ───────────── */

  {
    programId: 'vinif-sci-tech-grant',
    name: 'VinIF: Tài trợ dự án Khoa học và Công nghệ',
    shortName: 'VinIF',
    slug: 'vinif-tai-tro-khoa-hoc-cong-nghe',
    organizer: 'Quỹ Đổi mới sáng tạo VinIF',
    kind: 'RESEARCH_GRANT',
    website: 'https://vinif.org',
    publish: { status: 'PUBLISHED' },

    // Chương trình nhận hồ sơ theo đợt thường niên. Mức và lịch kỳ 2026 chưa
    // thấy công bố trên trang tổng quan, nên để chưa rõ thay vì đoán.
    edition: { label: 'Theo đợt thường niên', opensAt: null, closesAt: null },

    benefit: {
      capitalTypes: ['GRANT'],
      note: 'Mức tài trợ công bố theo từng thông báo hằng năm.',
    },

    eligibility: {
      stages: ['IDEA', 'PROTOTYPE', 'PRESEED', 'SEED'],
      geos: ['VN'],
      industries: ['AI', 'BIOTECH', 'HEALTHTECH', 'MATERIALS', 'SEMICONDUCTOR'],
      hardRequirements: [
        'Dự án có định hướng ứng dụng và tính khoa học rõ ràng',
        'Chủ nhiệm và đơn vị chủ trì đủ điều kiện theo thông báo từng đợt',
      ],
    },

    process: { applyUrl: 'https://vinif.org/' },

    content: {
      summary:
        'Chương trình tài trợ dự án khoa học công nghệ định hướng ứng dụng tại '
        + 'Việt Nam, không hoàn lại và không lấy cổ phần.',
      tips: [
        'Đối tượng cốt lõi là nhà khoa học, viện và trường. Doanh nghiệp thuần '
        + 'thương mại có thể không đủ điều kiện nếu không có cấu trúc nghiên cứu '
        + 'phù hợp hoặc đối tác viện trường.',
      ],
    },

    provenance: {
      sources: [
        { url: 'https://vinif.org/', fetchedAt: '2026-09-11', note: 'Trang chính thức của quỹ' },
      ],
      lastVerifiedAt: '2026-09-11',
    },
  },

  {
    programId: 'developpp-classic',
    name: 'develoPPP Classic',
    shortName: 'develoPPP',
    slug: 'developpp-classic',
    organizer: 'Bộ Hợp tác Kinh tế và Phát triển Liên bang Đức',
    kind: 'MATCHING_GRANT',
    website: 'https://www.developpp.de',
    publish: { status: 'PUBLISHED' },

    // Kỳ hiện tại đóng ngày 30/09. Năm cụ thể cần xác minh lại trước mỗi đợt.
    edition: { label: 'Kỳ đang mở', opensAt: null, closesAt: '2026-09-30' },

    benefit: {
      capitalTypes: ['GRANT'],
      max: eur(2_000_000),
      note: 'Tài trợ công tối đa 50% chi phí dự án, phần còn lại doanh nghiệp đối ứng.',
    },

    eligibility: {
      stages: ['SEED', 'SERIES_A', 'LATER'],
      geos: ['VN'],
      industries: ['CLIMATE', 'AGRITECH', 'LOGISTICS', 'ENERGY'],
      hardRequirements: [
        'Dự án vừa có lợi ích phát triển vừa có tính thương mại',
        'Doanh nghiệp phải đối ứng ít nhất 50% chi phí dự án',
        'Yêu cầu về doanh thu và số năm hoạt động thay đổi theo từng đợt',
      ],
    },

    process: {
      applyUrl: 'https://www.developpp.de/',
      notes: 'Startup Việt Nam có thể cần đối tác hoặc pháp nhân đủ điều kiện tại nước tài trợ.',
    },

    content: {
      summary:
        'Chương trình tài trợ đối ứng của Đức cho dự án kinh doanh bền vững tại '
        + 'các thị trường đang phát triển, trong đó có Việt Nam.',
      tips: [
        'Đây là tài trợ đối ứng, không phải cấp toàn bộ. Doanh nghiệp phải chứng '
        + 'minh được khả năng bỏ phần vốn còn lại.',
      ],
    },


    // Doanh nghiệp đã qua chương trình — lấy từ công bố chính thức.
    // Chương trình không rót vốn đổi cổ phần, nên đây là "đã tham gia",
    // không phải "đã đầu tư"; giao diện gọi đúng như vậy.
    alumni: {
      source: 'https://www.developpp.de/en/projects-success-stories/',
      verifiedAt: '2026-09-27',
      note: 'develoPPP hợp tác với doanh nghiệp để làm dự án phát triển, nên đây là các dự án đã được tài trợ chứ không phải startup được rót vốn. Hai dự án ở Việt Nam, ba dự án còn lại trong khu vực để bạn hình dung phạm vi. Mô tả lấy theo đúng cách chương trình ghi trên trang.',
      items: [
        {
          name: 'Festo Didactic, Schneider Electric Việt Nam và IBS',
          whatTheyDo:
            'Lập trung tâm đào tạo nghề cho kỹ thuật viên điện gió và điện mặt trời tại Việt Nam.',
          industries: ['ENERGY', 'EDTECH'],
          geo: 'VN',
        },
        {
          name: 'Les Vergers du Mékong',
          whatTheyDo:
            'Phát triển vùng trồng và chế biến trái cây ở đồng bằng sông Cửu Long.',
          industries: ['AGRITECH', 'FOODBEV'],
          geo: 'VN',
        },
        {
          name: 'Nestlé',
          whatTheyDo:
            'Nâng mức canh tác bền vững cho nông hộ trồng cà phê ở Đông Nam Á.',
          industries: ['AGRITECH', 'FOODBEV'],
          geo: 'SEA',
        },
        {
          name: 'Continental AG',
          whatTheyDo:
            'Số hoá truy xuất nguồn gốc chuỗi cung ứng cao su tại Indonesia.',
          industries: ['AGRITECH', 'LOGISTICS'],
          geo: 'SEA',
        },
        {
          name: 'BASF, Procter & Gamble và Cargill',
          whatTheyDo:
            'Thu mua dầu dừa bền vững tại Philippines và Indonesia.',
          industries: ['AGRITECH', 'FOODBEV'],
          geo: 'SEA',
        },
      ],
    },
    provenance: {
      sources: [
        { url: 'https://www.developpp.de/', fetchedAt: '2026-09-11', note: 'Trang chương trình chính thức' },
      ],
      lastVerifiedAt: '2026-09-11',
    },
  },

  {
    programId: 'global-innovation-fund',
    name: 'Global Innovation Fund',
    shortName: 'GIF',
    slug: 'global-innovation-fund',
    organizer: 'Global Innovation Fund',
    kind: 'BLENDED_IMPACT',
    website: 'https://www.globalinnovation.fund',
    publish: { status: 'PUBLISHED' },

    edition: { label: 'Cửa sổ gần nhất', opensAt: null, closesAt: '2025-01-15' },

    benefit: {
      capitalTypes: ['GRANT', 'EQUITY', 'DEBT'],
      note: 'Mức và hình thức thay đổi theo giai đoạn của giải pháp.',
    },

    eligibility: {
      stages: ['SEED', 'SERIES_A', 'LATER'],
      geos: ['GLOBAL'],
      industries: ['CLIMATE', 'HEALTHTECH', 'EDTECH', 'AGRITECH'],
      hardRequirements: [
        'Giải pháp nhắm tới một thách thức phát triển cụ thể',
        'Đã có bằng chứng về hiệu quả và tiềm năng mở rộng',
      ],
    },

    process: { applyUrl: 'https://www.globalinnovation.fund/apply-for-funding/' },

    content: {
      summary:
        'Quỹ kết hợp tài trợ, vốn cổ phần và cho vay cho các giải pháp giải '
        + 'quyết thách thức phát triển, nhận hồ sơ theo cửa sổ công bố.',
    },

    provenance: {
      sources: [
        { url: 'https://www.globalinnovation.fund/apply-for-funding/', fetchedAt: '2026-09-11', note: 'Trang nộp hồ sơ' },
      ],
      lastVerifiedAt: '2026-09-11',
    },
  },

  /* ───────────── Chương trình doanh nghiệp tại Việt Nam ───────────── */

  {
    programId: 'l-camp-vietnam',
    name: 'L-CAMP Vietnam',
    shortName: 'L-CAMP',
    slug: 'l-camp-vietnam',
    organizer: 'Lotte Ventures Vietnam',
    kind: 'OPEN_INNOVATION',
    website: 'https://lotteventures.vn',
    publish: { status: 'PUBLISHED' },

    edition: { label: 'Kỳ 2026', opensAt: null, closesAt: '2026-06-19' },

    benefit: {
      capitalTypes: ['IN_KIND'],
      note:
        'Triển khai thử nghiệm cùng các đơn vị trong hệ sinh thái LOTTE tại Việt '
        + 'Nam. Ngân sách thử nghiệm không công bố trên nguồn chính thức.',
    },

    eligibility: {
      stages: ['SEED', 'SERIES_A'],
      geos: ['VN'],
      industries: ['ECOMMERCE', 'RETAILTECH', 'FOODBEV', 'LOGISTICS', 'FINTECH', 'AI', 'SAAS_B2B'],
      hardRequirements: [
        'Sản phẩm đủ hoàn thiện để triển khai thử nghiệm thật',
        'Bài toán khớp nhu cầu của một đơn vị thuộc LOTTE',
      ],
    },

    process: { applyUrl: 'https://lotteventures.vn/' },

    content: {
      summary:
        'Chương trình đổi mới mở giữa startup Việt Nam và hệ sinh thái LOTTE, '
        + 'tập trung vào triển khai thử nghiệm thực tế.',
      tips: [
        'Triển khai thử nghiệm không đồng nghĩa hợp đồng mua hàng hay khoản đầu '
        + 'tư. Đây là cơ hội có khách hàng doanh nghiệp lớn, không phải cơ hội gọi vốn.',
      ],
    },


    // Doanh nghiệp đã qua chương trình — lấy từ công bố chính thức.
    // Chương trình không rót vốn đổi cổ phần, nên đây là "đã tham gia",
    // không phải "đã đầu tư"; giao diện gọi đúng như vậy.
    alumni: {
      source: 'https://lotteventures.vn/portfolio/l-camp/',
      verifiedAt: '2026-09-27',
      note: 'Khoá 1 năm 2026 gồm năm doanh nghiệp: CNV CDP, Parsley, HapBio, DayLaDau và Loudio. Trang của chương trình chỉ mô tả hai trong số đó nên ba tên còn lại chỉ nêu ở đây. Tham gia chương trình là được chạy thử cùng hệ sinh thái LOTTE và có thể dẫn tới đầu tư, không phải đã được rót vốn.',
      items: [
        {
          name: 'Parsley',
          whatTheyDo:
            'Nhóm thương hiệu làm đẹp dựa trên bằng chứng khoa học, dẫn đầu là Men Stay Simplicity.',
          industries: ['RETAILTECH'],
          geo: 'VN',
          roundDate: '2026',
        },
        {
          name: 'DayLaDau',
          whatTheyDo:
            'Nền tảng đặt chỗ nghỉ ngắn ngày gần nơi ở cho người trẻ.',
          industries: ['TRAVEL'],
          geo: 'VN',
          roundDate: '2026',
        },
      ],
    },
    provenance: {
      sources: [
        {
          url: 'https://lotteventures.vn/lotte-ventures-vietnam-officially-launched-the-l-camp-vietnam-2026-program-to-promote-collaborative-pilot-programs-between-vietnamese-startups-and-the-lotte-group-ecosystem/',
          fetchedAt: '2026-09-12',
          note: 'Thông cáo chính thức chương trình kỳ 2026',
        },
      ],
      lastVerifiedAt: '2026-09-12',
    },
  },

  {
    programId: 'shinhan-innoboost',
    name: 'Global Shinhan InnoBoost',
    shortName: 'InnoBoost',
    slug: 'shinhan-innoboost',
    organizer: 'Shinhan Future\u2019s Lab Việt Nam',
    kind: 'OPEN_INNOVATION',
    website: 'https://futureslab.com.vn/innoboost/',
    publish: { status: 'PUBLISHED' },

    edition: { label: 'Kỳ 2026', opensAt: '2026-04-01', closesAt: '2026-05-03' },

    benefit: {
      capitalTypes: ['GRANT', 'IN_KIND'],
      max: trieu(200),
      note: 'Mức hỗ trợ theo thông tin chương trình kỳ 2026, kèm cơ hội triển khai thử nghiệm.',
    },

    eligibility: {
      stages: ['PROTOTYPE', 'SEED', 'SERIES_A'],
      geos: ['VN'],
      industries: ['FINTECH', 'AI', 'MARTECH', 'ECOMMERCE'],
      hardRequirements: [
        'Đã có sản phẩm chạy được và dấu hiệu nhu cầu',
        'Có bài toán cụ thể để triển khai thử nghiệm với một đơn vị thuộc Shinhan',
      ],
    },

    process: { applyUrl: 'https://futureslab.com.vn/innoboost/' },

    content: {
      summary:
        'Chương trình đổi mới mở của Shinhan tại Việt Nam, kết hợp hỗ trợ tài '
        + 'chính và triển khai thử nghiệm với các đơn vị trong tập đoàn.',
    },


    // Doanh nghiệp đã qua chương trình — lấy từ công bố chính thức.
    // Chương trình không rót vốn đổi cổ phần, nên đây là "đã tham gia",
    // không phải "đã đầu tư"; giao diện gọi đúng như vậy.
    alumni: {
      source: 'https://futureslab.com.vn/innoboost/',
      verifiedAt: '2026-09-27',
      note: 'Bốn doanh nghiệp được chương trình nêu trong phần chia sẻ của cựu thành viên, không phải toàn bộ danh sách các khoá. Chương trình cố vấn và kết nối với hệ sinh thái Shinhan, không đổi cổ phần.',
      items: [
        {
          name: 'Filum.ai',
          whatTheyDo:
            'Nền tảng quản trị trải nghiệm khách hàng vận hành bằng AI.',
          industries: ['SAAS_B2B', 'AI'],
          geo: 'VN',
        },
        {
          name: '1Long',
          whatTheyDo:
            'Ứng dụng tích luỹ và đầu tư số tiền nhỏ cho người mới bắt đầu.',
          industries: ['FINTECH'],
          geo: 'VN',
        },
        {
          name: 'Papaya Insurtech',
          whatTheyDo:
            'Hạ tầng số cho bảo hiểm sức khoẻ của doanh nghiệp.',
          industries: ['INSURTECH', 'HEALTHTECH'],
          geo: 'VN',
        },
        {
          name: 'FLES',
          whatTheyDo:
            'Thu thập phản hồi khách hàng và nghiên cứu thị trường cho doanh nghiệp.',
          industries: ['MARTECH'],
        },
      ],
    },
    provenance: {
      sources: [
        { url: 'https://futureslab.com.vn/innoboost/', fetchedAt: '2026-09-12', note: 'Trang chương trình chính thức' },
      ],
      lastVerifiedAt: '2026-09-12',
    },
  },

  {
    programId: 'qualcomm-vietnam-innovation-challenge',
    name: 'Qualcomm Vietnam Innovation Challenge',
    shortName: 'QVIC',
    slug: 'qualcomm-vietnam-innovation-challenge',
    organizer: 'Qualcomm',
    kind: 'PRIZE_CHALLENGE',
    website: 'https://www.qualcomm.com/company/locations/vietnam',
    publish: { status: 'PUBLISHED' },

    edition: { label: 'Kỳ 2026', opensAt: null, closesAt: null },

    benefit: {
      capitalTypes: ['GRANT', 'IN_KIND'],
      max: usd(100_000),
      note:
        'Giải thưởng ba đội dẫn đầu lần lượt 100, 75 và 50 nghìn USD theo công bố '
        + 'của chương trình, kèm chương trình ươm tạo và hỗ trợ về sở hữu trí tuệ.',
    },

    eligibility: {
      stages: ['PROTOTYPE', 'PRESEED', 'SEED'],
      geos: ['VN'],
      industries: ['AI', 'SEMICONDUCTOR', 'ROBOTICS', 'MOBILITY'],
      hardRequirements: [
        'Có sản phẩm công nghệ hoặc tài sản trí tuệ riêng',
        'Đủ năng lực tham gia chương trình ươm tạo kéo dài',
      ],
    },

    process: {
      applyUrl: 'https://www.qualcomm.com/company/locations/vietnam',
      notes: 'Cần xác minh đường dẫn biểu mẫu cho kỳ tiếp theo.',
    },

    content: {
      summary:
        'Cuộc thi ươm tạo công nghệ dành cho startup tại Việt Nam, tập trung vào '
        + 'phần cứng, AI và công nghệ lõi.',
    },


    // Doanh nghiệp đã qua chương trình — lấy từ công bố chính thức.
    // Chương trình không rót vốn đổi cổ phần, nên đây là "đã tham gia",
    // không phải "đã đầu tư"; giao diện gọi đúng như vậy.
    alumni: {
      source: 'https://innoex.vn/qualcomm-announces-winners-for-qualcomm-vietnam-innovation-challenge-2025-at-innoex/',
      verifiedAt: '2026-09-26',
      note: 'Đây là các đội đoạt giải chung kết hai mùa gần nhất, không phải toàn bộ doanh nghiệp từng dự thi. Chương trình trao giải thưởng tiền mặt và quyền truy cập mạng lưới của Qualcomm, không đổi cổ phần.',
      items: [
        {
          name: 'Rainscales Vietnam',
          whatTheyDo:
            'Nền tảng eMagicEyes kết hợp thị giác máy tính và kinh nghiệm người vận hành để giám sát an toàn nhà máy. Giải nhất 2025.',
          industries: ['AI', 'MANUFACTURING'],
          geo: 'VN',
          roundDate: '2025',
        },
        {
          name: 'Viet Dynamic',
          whatTheyDo:
            'Hệ thống sản xuất thông minh ứng dụng AI để tối ưu vận hành nhà máy. Giải nhì 2025.',
          industries: ['AI', 'MANUFACTURING'],
          geo: 'VN',
          roundDate: '2025',
        },
        {
          name: 'Enfarm Agritech',
          whatTheyDo:
            'Cảm biến đo dinh dưỡng đất theo thời gian thực kèm trợ lý AI khuyến nghị bón phân. Giải ba 2025.',
          industries: ['AGRITECH', 'AI'],
          geo: 'VN',
          roundDate: '2025',
        },
        {
          name: 'Vbee',
          whatTheyDo:
            'Nền tảng AI hội thoại cho tổng đài và ứng dụng giọng nói tiếng Việt. Giải nhất 2024.',
          industries: ['AI'],
          geo: 'VN',
          roundDate: '2024',
        },
        {
          name: 'HSPTek',
          whatTheyDo:
            'Thiết bị đeo giám sát tĩnh điện theo thời gian thực cho an toàn nhà xưởng. Giải nhì 2024.',
          industries: ['MANUFACTURING', 'ROBOTICS'],
          geo: 'VN',
          roundDate: '2024',
        },
        {
          name: 'Met EV',
          whatTheyDo:
            'Xe hai bánh điện giá phổ thông kèm giải pháp đổi pin điều phối bằng AI. Giải ba 2024.',
          industries: ['MOBILITY', 'AI'],
          geo: 'VN',
          roundDate: '2024',
        },
      ],
    },
    provenance: {
      sources: [
        { url: 'https://www.qualcomm.com/company/locations/vietnam', fetchedAt: '2026-09-12', note: 'Trang Qualcomm Việt Nam' },
      ],
      lastVerifiedAt: '2026-09-12',
    },
  },

  {
    programId: '100-plus-accelerator',
    name: '100+ Accelerator',
    shortName: '100+',
    slug: '100-plus-accelerator',
    organizer: 'AB InBev và các đối tác',
    kind: 'CORPORATE_ACCELERATOR',
    website: 'https://www.100accelerator.com',
    publish: { status: 'PUBLISHED' },

    edition: { label: 'Cohort 8', opensAt: null, closesAt: null },

    benefit: {
      capitalTypes: ['IN_KIND'],
      max: usd(100_000),
      note: 'Kinh phí cho triển khai thử nghiệm, theo công bố của chương trình.',
    },

    eligibility: {
      stages: ['SEED', 'SERIES_A'],
      geos: ['GLOBAL'],
      industries: ['CLIMATE', 'AGRITECH', 'LOGISTICS', 'MATERIALS'],
      hardRequirements: [
        'Giải pháp khớp một trong các bài toán bền vững chương trình công bố',
        'Sẵn sàng triển khai thử nghiệm với tập đoàn',
      ],
    },

    process: { applyUrl: 'https://www.100accelerator.com/' },

    content: {
      summary:
        'Chương trình tăng tốc do doanh nghiệp vận hành, tập trung vào các giải '
        + 'pháp bền vững triển khai được trong chuỗi cung ứng.',
    },

    provenance: {
      sources: [
        { url: 'https://www.100accelerator.com/', fetchedAt: '2026-09-12', note: 'Trang chương trình chính thức' },
      ],
      lastVerifiedAt: '2026-09-12',
    },
  },

  {
    programId: 'pepsico-greenhouse-apac',
    name: 'PepsiCo Greenhouse Accelerator châu Á Thái Bình Dương',
    shortName: 'Greenhouse APAC',
    slug: 'pepsico-greenhouse-apac',
    organizer: 'PepsiCo',
    kind: 'CORPORATE_ACCELERATOR',
    website: 'https://greenhouseaccelerator.com',
    publish: { status: 'PUBLISHED' },

    edition: { label: 'Kỳ 2026', opensAt: null, closesAt: null },

    benefit: {
      capitalTypes: ['IN_KIND'],
      note: 'Mức hỗ trợ tiền mặt chưa xác nhận được trên nguồn chính thức đang công khai.',
    },

    eligibility: {
      stages: ['SEED', 'SERIES_A'],
      geos: ['APAC'],
      industries: ['CLIMATE', 'AGRITECH', 'LOGISTICS', 'MATERIALS'],
      hardRequirements: [
        'Giải pháp sẵn sàng triển khai trong chuỗi cung ứng của tập đoàn',
      ],
    },

    process: { applyUrl: 'https://greenhouseaccelerator.com/' },

    content: {
      summary:
        'Chương trình tăng tốc khu vực châu Á Thái Bình Dương của PepsiCo, tập '
        + 'trung vào bền vững và chuỗi cung ứng.',
    },


    // Doanh nghiệp đã qua chương trình — lấy từ công bố chính thức.
    // Chương trình không rót vốn đổi cổ phần, nên đây là "đã tham gia",
    // không phải "đã đầu tư"; giao diện gọi đúng như vậy.
    alumni: {
      source: 'https://theinvestor.vn/vietnamese-startups-chosen-for-pepsicos-top-10-greenhouse-accelerator-apac-d9973.html',
      verifiedAt: '2026-09-27',
      note: 'Khoá 2024 có hai doanh nghiệp Việt Nam vào vòng chung kết; khoá 2025 không có doanh nghiệp Việt Nam nào. Chương trình trao giải thưởng và cơ hội chạy thử cùng PepsiCo, không đổi cổ phần.',
      items: [
        {
          name: 'Alternō',
          whatTheyDo:
            'Lưu trữ nhiệt bằng cát, thay nhiên liệu đốt trong sấy nông sản và cấp nhiệt công nghiệp.',
          industries: ['CLIMATE', 'ENERGY'],
          geo: 'VN',
          roundDate: '2024',
        },
        {
          name: 'Grac',
          whatTheyDo:
            'Phần mềm quản lý thu gom rác và tái chế cho chính quyền địa phương và doanh nghiệp.',
          industries: ['CLIMATE'],
          geo: 'VN',
          roundDate: '2024',
        },
        {
          name: 'Beijing AIForce Technology',
          whatTheyDo:
            'Ứng dụng AI cho các bài toán bền vững trong chuỗi giá trị thực phẩm. Quán quân khoá 2025.',
          industries: ['AI', 'CLIMATE'],
          geo: 'APAC',
          roundDate: '2025',
        },
        {
          name: 'Circular Unite',
          whatTheyDo:
            'Quản lý rác thải bằng AI và sàn biến rác thành nguyên liệu đầu vào.',
          industries: ['CLIMATE'],
          geo: 'SEA',
          roundDate: '2025',
        },
        {
          name: 'Bali Waste Cycle',
          whatTheyDo:
            'Dịch vụ thu gom và tái chế rác lưu động.',
          industries: ['CLIMATE'],
          geo: 'SEA',
          roundDate: '2025',
        },
        {
          name: 'Calyx.eco',
          whatTheyDo:
            'Đánh giá vòng đời sản phẩm bằng AI cho nông nghiệp bền vững.',
          industries: ['AGRITECH', 'CLIMATE'],
          geo: 'APAC',
          roundDate: '2025',
        },
      ],
    },
    provenance: {
      sources: [
        { url: 'https://greenhouseaccelerator.com/', fetchedAt: '2026-09-12', note: 'Trang chương trình chính thức' },
      ],
      lastVerifiedAt: '2026-09-12',
    },
  },

  {
    programId: 'google-startups-accelerator-sea',
    name: 'Google for Startups Accelerator: Đông Nam Á',
    shortName: 'GFSA Đông Nam Á',
    slug: 'google-for-startups-accelerator-sea',
    organizer: 'Google for Startups',
    kind: 'EQUITY_FREE_ACCELERATOR',
    website: 'https://startup.google.com/programs/accelerator/southeast-asia/',
    publish: { status: 'PUBLISHED' },

    edition: { label: 'Kỳ 2026', opensAt: null, closesAt: '2026-06-20' },

    benefit: {
      capitalTypes: ['IN_KIND'],
      note:
        'Không có khoản đầu tư tiền mặt và không lấy cổ phần. Hỗ trợ gồm cố vấn '
        + 'kỹ thuật và tín dụng đám mây tuỳ điều kiện.',
    },

    eligibility: {
      stages: ['SEED', 'SERIES_A'],
      geos: ['SEA'],
      industries: ['AI', 'SAAS_B2B'],
      hardRequirements: [
        'Startup tại Đông Nam Á đã có dấu hiệu thị trường',
        'Sản phẩm dùng AI hoặc công nghệ lõi',
        'Người sáng lập và trưởng nhóm kỹ thuật tham gia đầy đủ chương trình',
      ],
    },

    process: { applyUrl: 'https://startup.google.com/programs/accelerator/southeast-asia/' },

    content: {
      summary:
        'Chương trình tăng tốc không lấy cổ phần của Google dành cho startup '
        + 'Đông Nam Á, nhấn mạnh cố vấn kỹ thuật.',
      tips: [
        'Không lấy cổ phần là điểm đáng chú ý. Cái giá phải trả là thời gian của '
        + 'người sáng lập và trưởng nhóm kỹ thuật trong suốt chương trình.',
      ],
    },


    // Doanh nghiệp đã qua chương trình — lấy từ công bố chính thức.
    // Chương trình không rót vốn đổi cổ phần, nên đây là "đã tham gia",
    // không phải "đã đầu tư"; giao diện gọi đúng như vậy.
    alumni: {
      source: 'https://startup.google.com/programs/accelerator/southeast-asia/',
      verifiedAt: '2026-09-26',
      note: 'Chương trình đã có hơn 30 doanh nghiệp Việt Nam đi qua; đây chỉ là một phần được chọn để bạn hình dung. Mô tả lấy theo đúng cách chương trình ghi trên trang. Đây là chương trình cố vấn và hỗ trợ kỹ thuật, không rót vốn.',
      items: [
        {
          name: 'Fundiin',
          whatTheyDo:
            'Giải pháp mua trước trả sau cho thương mại điện tử và bán lẻ.',
          industries: ['FINTECH'],
          geo: 'VN',
        },
        {
          name: 'Gimo',
          whatTheyDo:
            'Cho người lao động nhận phần lương đã làm trước kỳ trả lương.',
          industries: ['FINTECH', 'HRTECH'],
          geo: 'VN',
        },
        {
          name: 'Docosan',
          whatTheyDo:
            'Sàn đặt lịch khám và dịch vụ y tế.',
          industries: ['HEALTHTECH'],
          geo: 'VN',
        },
        {
          name: 'Filum.ai',
          whatTheyDo:
            'Nền tảng quản trị trải nghiệm khách hàng vận hành bằng AI.',
          industries: ['SAAS_B2B', 'AI'],
          geo: 'VN',
        },
        {
          name: 'Foodmap Asia',
          whatTheyDo:
            'Sàn B2B và B2C kết nối nông dân, nhà sản xuất với người mua.',
          industries: ['AGRITECH', 'ECOMMERCE'],
          geo: 'VN',
        },
        {
          name: 'TopCV',
          whatTheyDo:
            'Nền tảng tuyển dụng và hồ sơ ứng tuyển trực tuyến.',
          industries: ['HRTECH'],
          geo: 'VN',
        },
        {
          name: 'INKR',
          whatTheyDo:
            'Nền tảng cho tác giả và nhà xuất bản truyện tranh.',
          industries: ['MEDIA'],
          geo: 'VN',
        },
        {
          name: 'Vbee',
          whatTheyDo:
            'Doanh nghiệp AI hội thoại tiếng Việt.',
          industries: ['AI'],
          geo: 'VN',
        },
        {
          name: 'BUSMAP',
          whatTheyDo:
            'Dịch vụ tra cứu và di chuyển bằng phương tiện công cộng cho các nước ASEAN.',
          industries: ['MOBILITY'],
          geo: 'VN',
        },
        {
          name: 'enFarm Agritech',
          whatTheyDo:
            'Phần mềm tư vấn canh tác bằng AI và cảm biến đất thông minh.',
          industries: ['AGRITECH', 'AI'],
          geo: 'VN',
        },
      ],
    },
    provenance: {
      sources: [
        {
          url: 'https://startup.google.com/programs/accelerator/southeast-asia/',
          fetchedAt: '2026-09-12',
          note: 'Trang chương trình chính thức',
        },
      ],
      lastVerifiedAt: '2026-09-12',
    },
  },
  {
    programId: 'block71-vietnam',
    name: 'BLOCK71 Vietnam (NUS)',
    shortName: 'BLOCK71 Vietnam',
    slug: 'block71-vietnam',
    organizer: 'NUSX (NUS Enterprise, Đại học Quốc gia Singapore)',
    kind: 'EQUITY_FREE_ACCELERATOR',
    website: 'https://nusx.edu.sg/startups/b71/vietnam',
    publish: { status: 'PUBLISHED' },
    edition: { label: 'Ươm tạo nhận quanh năm', rolling: true },
    benefit: {
      capitalTypes: ['IN_KIND', 'GRANT'],
      // UniVentures: 25.000 USD cho mỗi đội trong top 10, theo trang BLOCK71 Vietnam chính thức.
      max: usd(25000),
      note: 'Chương trình ươm tạo không lấy cổ phần: cố vấn, lộ trình, hỗ trợ gọi vốn, chỗ làm việc tại BLOCK71 TP.HCM. '
        + 'Riêng cuộc thi UniVentures trao 25.000 USD và 3 tháng ươm tạo cho 10 đội đứng đầu.',
    },
    eligibility: {
      stages: ['PROTOTYPE', 'PRESEED', 'SEED'],
      geos: ['VN'],
      industries: [],
      hardRequirements: [
        'Startup giai đoạn sớm đang xây dựng tại Việt Nam, startup Việt Nam muốn ra nước ngoài, hoặc startup nước ngoài muốn vào Việt Nam',
        'UniVentures chỉ dành cho sinh viên, cán bộ và người mới tốt nghiệp các trường đại học Việt Nam',
      ],
    },
    process: {
      applyUrl: 'https://app.block71.co',
    },
    content: {
      summary:
        'Mạng lưới khởi nghiệp của Đại học Quốc gia Singapore (NUS), có mặt tại 11 địa điểm ở 6 nước, hoạt động tại TP.HCM từ năm 2020. '
        + 'BLOCK71 Vietnam ươm tạo startup giai đoạn sớm, không lấy cổ phần, founder giữ 100% sở hữu, '
        + 'và kết nối với nhà đầu tư, doanh nghiệp cùng mạng lưới NUS ở Singapore và khu vực.',
      tips: [
        'Đây không phải quỹ: BLOCK71 không công bố việc tự đầu tư vào startup. Giá trị chính là mạng lưới NUS, cố vấn và đường sang Singapore.',
        'UniVentures (mùa đầu kết thúc 01/2026, hơn 1.400 hồ sơ) trao 25.000 USD và 3 tháng ươm tạo cho 10 đội; 2 đội đứng đầu được ươm tạo tiếp tại BLOCK71 Singapore. '
        + 'Golden Gate Ventures tài trợ giải thưởng. Theo dõi trang chính thức để biết mùa tiếp theo.',
        'Ươm tạo xét quanh năm và quyết định tại Việt Nam. Hãy nói rõ bạn cần gì cụ thể: khách hàng doanh nghiệp, nhà đầu tư Singapore hay mở rộng sang thị trường khác.',
        'Ngoài ra có chương trình 84X cho startup đã trưởng thành hơn và hỗ trợ mở rộng thị trường; trang chính thức chưa nêu chi tiết.',
      ],
    },
    provenance: {
      sources: [
        { url: 'https://nusx.edu.sg/startups/b71/vietnam', fetchedAt: '2026-10-14', note: 'Trang BLOCK71 Vietnam: ươm tạo, UniVentures, không lấy cổ phần' },
        { url: 'https://nusx.edu.sg/startups/b71', fetchedAt: '2026-10-14', note: 'Tổng quan mạng lưới BLOCK71 và đường dẫn nộp hồ sơ app.block71.co' },
        { url: 'https://en.vneconomy.vn/ten-vietnamese-university-startups-selected-for-incubation-with-support-from-singaporean-partners.htm', fetchedAt: '2026-10-14', note: 'Kết quả UniVentures mùa đầu (31/01/2026)' },
      ],
      lastVerifiedAt: '2026-10-14',
    },
  },
];

/** Danh sách hiển thị: dữ liệu gốc + bản trên Firestore (kéo về lúc build). */
export const PROGRAMS = gopChuongTrinh(PROGRAMS_GOC, REMOTE_PROGRAMS);

export const SO_CHUONG_TRINH = PROGRAMS.filter(
  (p) => p.publish?.status === 'PUBLISHED'
).length;

export function programBySlug(slug) {
  return PROGRAMS.find((p) => p.slug === slug) || null;
}
